import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { HomeIcon, ArrowLeft, FileQuestion } from "lucide-react";

const NotFound = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 dark:bg-zinc-900 px-4 py-24 sm:px-6 lg:px-8">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 border border-zinc-200 dark:border-zinc-700/50 rounded-full"></div>
        <div className="absolute bottom-20 left-20 w-64 h-64 border border-zinc-200 dark:border-zinc-700/50 rounded-full"></div>
        <div className="absolute top-1/4 left-1/3 w-40 h-40 bg-gradient-to-br from-blue-500/5 to-violet-500/5 rounded-full blur-3xl"></div>
      </div>

      <motion.div 
        className="relative max-w-md w-full text-center"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* 404 Number */}
        <motion.div variants={itemVariants} className="relative">
          <div className="text-[120px] sm:text-[150px] font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-violet-600 leading-none">
            404
          </div>
          
          {/* Decorative orbit */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 border border-zinc-200 dark:border-zinc-700 rounded-full opacity-20"></div>
          
          {/* Icon */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="bg-white dark:bg-zinc-800 p-4 rounded-full shadow-lg border border-zinc-200 dark:border-zinc-700">
              <FileQuestion size={32} className="text-zinc-400 dark:text-zinc-500" />
            </div>
          </div>
        </motion.div>
        
        {/* Text content */}
        <motion.h1 
          variants={itemVariants}
          className="mt-8 text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-white"
        >
          Page not found
        </motion.h1>
        
        <motion.p 
          variants={itemVariants}
          className="mt-4 text-zinc-600 dark:text-zinc-400"
        >
          The page you're looking for doesn't exist or has been moved.
          Let's get you back on track.
        </motion.p>
        
        {/* Buttons */}
        <motion.div 
          variants={itemVariants}
          className="mt-8 flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white rounded-lg shadow-md">
            <Link to="/" className="flex items-center gap-2">
              <HomeIcon size={18} />
              Return Home
            </Link>
          </Button>
          
          <Button asChild variant="outline" size="lg" className="rounded-lg border-2 border-zinc-200 dark:border-zinc-700">
            <Link to="/projects" className="flex items-center gap-2">
              <ArrowLeft size={18} />
              View Projects
            </Link>
          </Button>
        </motion.div>
        
        {/* Footer note */}
        <motion.p 
          variants={itemVariants} 
          className="mt-12 text-sm text-zinc-500 dark:text-zinc-500"
        >
          If you believe this is a mistake, please <Link to="/contact" className="text-blue-600 dark:text-blue-400 hover:underline">contact me</Link>.
        </motion.p>
      </motion.div>
    </div>
  );
};

export default NotFound;